<script setup lang="ts">
import VideoCallVideo from "~/components/video-call/VideoCallVideo.vue";

withDefaults(
  defineProps<{
    guestName: string;
    stream: MediaStream | null;
    hostName?: string;
    cameraEnabled: boolean;
    microphoneEnabled: boolean;
    errorMessage?: string;
  }>(),
  {
    hostName: "O professor",
    errorMessage: "",
  },
);

const emit = defineEmits<{
  "toggle-microphone": [];
  "toggle-camera": [];
  "return-to-call": [];
  cancel: [];
}>();
</script>

<template>
  <section
    class="video-call-guest-waiting"
    data-testid="video-call-guest-waiting-widget"
  >
    <header class="video-call-guest-waiting__header">
      <div class="video-call-guest-waiting__status" role="status">
        <span aria-hidden="true" />
        <div>
          <small>Sala de espera</small>
          <strong>Aguardando aprovação</strong>
        </div>
      </div>
      <button
        type="button"
        class="video-call-guest-waiting__icon-button"
        aria-label="Voltar para a chamada"
        title="Voltar para a chamada"
        @click="emit('return-to-call')"
      >
        <i class="mdi mdi-arrow-expand" aria-hidden="true" />
      </button>
    </header>

    <p class="video-call-guest-waiting__message">
      {{ hostName }} recebeu seu pedido. A espera continua enquanto você navega.
    </p>

    <div class="video-call-guest-waiting__preview">
      <VideoCallVideo
        :stream="stream"
        :name="guestName"
        local
        :camera-enabled="cameraEnabled"
        :microphone-enabled="microphoneEnabled"
      />
    </div>

    <p v-if="errorMessage" class="video-call-guest-waiting__error" role="alert">
      {{ errorMessage }}
    </p>

    <footer class="video-call-guest-waiting__footer">
      <div class="video-call-guest-waiting__media-controls">
        <button
          type="button"
          class="video-call-guest-waiting__icon-button"
          :class="{
            'video-call-guest-waiting__icon-button--off': !microphoneEnabled,
          }"
          :aria-label="
            microphoneEnabled ? 'Desligar microfone' : 'Ligar microfone'
          "
          @click="emit('toggle-microphone')"
        >
          <i
            class="mdi"
            :class="microphoneEnabled ? 'mdi-microphone' : 'mdi-microphone-off'"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          class="video-call-guest-waiting__icon-button"
          :class="{
            'video-call-guest-waiting__icon-button--off': !cameraEnabled,
          }"
          :aria-label="cameraEnabled ? 'Desligar câmera' : 'Ligar câmera'"
          @click="emit('toggle-camera')"
        >
          <i
            class="mdi"
            :class="cameraEnabled ? 'mdi-video' : 'mdi-video-off-outline'"
            aria-hidden="true"
          />
        </button>
      </div>
      <div class="video-call-guest-waiting__actions">
        <button
          type="button"
          class="video-call-guest-waiting__cancel-button"
          @click="emit('cancel')"
        >
          Cancelar participação
        </button>
        <button
          type="button"
          class="video-call-guest-waiting__return-button"
          @click="emit('return-to-call')"
        >
          Voltar para a chamada
        </button>
      </div>
    </footer>
  </section>
</template>

<style scoped lang="scss">
.video-call-guest-waiting {
  box-sizing: border-box;
  width: min(400px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  padding: 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #dbe1ef;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 22px 60px rgba(17, 30, 55, 0.24);
  color: #26334d;
}

.video-call-guest-waiting__header,
.video-call-guest-waiting__footer,
.video-call-guest-waiting__status,
.video-call-guest-waiting__media-controls,
.video-call-guest-waiting__actions {
  display: flex;
  align-items: center;
}

.video-call-guest-waiting__header {
  justify-content: space-between;
  gap: 12px;
}

.video-call-guest-waiting__status {
  min-width: 0;
  gap: 9px;

  > span {
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #3156ff;
    box-shadow: 0 0 0 4px rgba(49, 86, 255, 0.12);
  }

  > div {
    display: grid;
    min-width: 0;
  }

  small {
    color: #6f7b91;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  strong {
    overflow: hidden;
    color: #17233c;
    font-size: 0.9rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.video-call-guest-waiting__message {
  margin: 10px 0;
  color: #69758b;
  font-size: 0.74rem;
  line-height: 1.4;
}

.video-call-guest-waiting__preview {
  overflow: hidden;
  border-radius: 13px;

  :deep(.video-tile),
  :deep(.video-tile video),
  :deep(.video-tile .video-tile__placeholder) {
    min-height: 118px;
  }

  :deep(.video-tile) {
    height: 118px;
  }
}

.video-call-guest-waiting__error {
  margin: 10px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff0f1;
  color: #a51f34;
  font-size: 0.72rem;
  line-height: 1.35;
}

.video-call-guest-waiting__footer {
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
}

.video-call-guest-waiting__media-controls,
.video-call-guest-waiting__actions {
  gap: 7px;
}

.video-call-guest-waiting__icon-button {
  display: grid;
  place-items: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: #eef1f7;
  color: #273550;
  font: inherit;
  cursor: pointer;
}

.video-call-guest-waiting__icon-button--off {
  background: #fff0f1;
  color: #b00020;
}

.video-call-guest-waiting__cancel-button,
.video-call-guest-waiting__return-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 7px 10px;
  border-radius: 9px;
  font: inherit;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
}

.video-call-guest-waiting__cancel-button {
  border: 1px solid #ccd4e2;
  background: #fff;
  color: #4f5d75;
}

.video-call-guest-waiting__return-button {
  border: 1px solid #3156ff;
  background: #3156ff;
  color: #fff;
}

@media (max-width: 600px) {
  .video-call-guest-waiting__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .video-call-guest-waiting__media-controls {
    justify-content: center;
  }

  .video-call-guest-waiting__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}
</style>
